import React, { useState } from 'react';

interface ChatModalProps {
  customerName: string;
  customerPhone: string;
  orderId: string;
  onClose: () => void;
  onCall: () => void;
}

const ChatModal: React.FC<ChatModalProps> = ({ 
  customerName, 
  customerPhone, 
  orderId, 
  onClose,
  onCall 
}) => {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<Array<{sender: string, text: string, time: string}>>([]);
  const [lastMessageCount, setLastMessageCount] = React.useState(0);

  // Load chat history from API and poll for updates
  React.useEffect(() => {
    const loadChat = async () => {
      try {
        const token = localStorage.getItem('partnerToken');
        const response = await fetch(`https://web-production-79a0.up.railway.app/api/orders/${orderId}/chat`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          const formattedMessages = data.chatMessages.map((msg: any) => ({
            sender: msg.sender,
            text: msg.text,
            time: new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }));
          
          // Show notification if new customer message received
          if (formattedMessages.length > lastMessageCount && lastMessageCount > 0) {
            const latestMsg = formattedMessages[formattedMessages.length - 1];
            console.log('New message detected:', latestMsg);
            if (latestMsg.sender === 'Customer') {
              console.log('Customer message - showing notification');
              // Play notification sound
              const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSl+zPLTgjMGHm7A7+OZUQ8MW6vn7bdlHwU7k9n0y3gsBSh+y/DYiTYIGmy98dqZUhAJU6/p7qxYFQxGn+PwtWMcBz2T2PPQeysFKIHO89iKOAgZbL3y251SD');
              audio.play().catch(() => {});
              
              // Show browser notification
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('New message from Customer', {
                  body: latestMsg.text,
                  icon: '🛒'
                });
              }
              // Show alert popup
              setTimeout(() => {
                alert(`💬 New message from ${customerName}:\n${latestMsg.text}`);
              }, 100);
            }
          }
          
          setLastMessageCount(formattedMessages.length);
          setChatHistory(formattedMessages);
        }
      } catch (error) {
        console.error('Error loading chat:', error);
      }
    };
    
    loadChat();
    
    // Poll for new messages every 2 seconds
    const interval = setInterval(loadChat, 2000);
    
    return () => clearInterval(interval);
  }, [orderId]);

  const handleSendMessage = async () => {
    if (message.trim()) {
      try {
        const token = localStorage.getItem('partnerToken');
        await fetch(`https://web-production-79a0.up.railway.app/api/orders/${orderId}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            sender: 'Partner',
            text: message
          })
        });
        setMessage('');
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  };

  const quickMessages = [
    "Items are being packed",
    "Some items are not available",
    "Running 10 minutes late",
    "On my way to the store",
    "Need to confirm your address"
  ];

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>💬 Chat with Customer</h3>
            <p style={styles.subtitle}>{customerName} • {customerPhone}</p>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Order Info */}
        <div style={styles.orderInfo}>
          <span style={styles.orderLabel}>Order ID:</span>
          <span style={styles.orderId}>{orderId.substring(0, 8)}...</span>
        </div>

        {/* Chat History */}
        <div style={styles.chatContainer}>
          {chatHistory.length === 0 ? (
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>💭</span>
              <p style={styles.emptyText}>No messages yet</p>
              <p style={styles.emptySubtext}>Send a message or use a quick reply below</p>
            </div>
          ) : (
            chatHistory.map((msg, index) => (
              <div key={index} style={styles.messageWrapper}>
                <div style={styles.message}>
                  <div style={styles.messageSender}>{msg.sender}</div>
                  <div style={styles.messageText}>{msg.text}</div>
                  <div style={styles.messageTime}>{msg.time}</div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Quick Replies */}
        <div style={styles.quickReplies}>
          <p style={styles.quickTitle}>Quick Replies:</p>
          <div style={styles.quickButtons}>
            {quickMessages.map((msg, index) => (
              <button
                key={index}
                style={styles.quickBtn}
                onClick={() => setMessage(msg)}
              >
                {msg}
              </button>
            ))}
          </div>
        </div>

        {/* Message Input */}
        <div style={styles.inputContainer}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Type your message..."
            style={styles.input}
          />
          <button style={styles.sendBtn} onClick={handleSendMessage}>
            Send
          </button>
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          <button style={styles.callBtn} onClick={onCall}>
            📞 Call Customer
          </button>
          <p style={styles.note}>
            Note: Messages are stored locally. For urgent matters, please call the customer.
          </p>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
  },
  title: {
    margin: '0 0 5px 0',
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#333',
  },
  subtitle: {
    margin: 0,
    fontSize: '14px',
    color: '#666',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#999',
    padding: '0',
    width: '30px',
    height: '30px',
  },
  orderInfo: {
    padding: '10px 20px',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    gap: '10px',
    fontSize: '14px',
  },
  orderLabel: {
    color: '#666',
    fontWeight: '600',
  },
  orderId: {
    color: '#333',
    fontFamily: 'monospace',
  },
  chatContainer: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
    minHeight: '200px',
    maxHeight: '300px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#999',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '10px',
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 5px 0',
  },
  emptySubtext: {
    fontSize: '14px',
    margin: 0,
  },
  messageWrapper: {
    marginBottom: '15px',
  },
  message: {
    backgroundColor: '#f0f8ff',
    padding: '12px',
    borderRadius: '12px',
    maxWidth: '80%',
    border: '1px solid #e0f0ff',
  },
  messageSender: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#fc8019',
    marginBottom: '5px',
  },
  messageText: {
    fontSize: '14px',
    color: '#333',
    marginBottom: '5px',
    lineHeight: '1.4',
  },
  messageTime: {
    fontSize: '11px',
    color: '#999',
  },
  quickReplies: {
    padding: '15px 20px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
  },
  quickTitle: {
    margin: '0 0 10px 0',
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
  },
  quickButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  quickBtn: {
    padding: '6px 12px',
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '20px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#333',
    transition: 'all 0.2s',
  },
  inputContainer: {
    display: 'flex',
    gap: '10px',
    padding: '15px 20px',
    borderTop: '1px solid #e0e0e0',
  },
  input: {
    flex: 1,
    padding: '12px 15px',
    border: '1px solid #e0e0e0',
    borderRadius: '25px',
    fontSize: '14px',
    outline: 'none',
  },
  sendBtn: {
    padding: '12px 25px',
    backgroundColor: '#fc8019',
    color: 'white',
    border: 'none',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  actions: {
    padding: '15px 20px',
    borderTop: '1px solid #e0e0e0',
  },
  callBtn: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginBottom: '10px',
  },
  note: {
    margin: 0,
    fontSize: '12px',
    color: '#999',
    textAlign: 'center',
  },
};

export default ChatModal;
